import {TranslateIcon} from '@sanity/icons/Translate'
import {Button, Card, Checkbox, useClickOutsideEvent} from '@sanity/ui'
import {Popover} from '@sanity/ui/popover'
import {type FormEvent, useCallback, useRef, useState} from 'react'
import {type ObjectSchemaType} from 'sanity'
import {Text, Flex, Box} from 'ui5'

import {type LanguageFilterPluginOptions} from './types'
import {usePaneLanguages} from './usePaneLanguages'

export interface LanguageFilterMenuButtonProps {
  options: LanguageFilterPluginOptions

  schemaType: ObjectSchemaType
}

export function LanguageFilterMenuButton(props: LanguageFilterMenuButtonProps) {
  const {options} = props
  const defaultLanguages = options.supportedLanguages.filter((l) =>
    options.defaultLanguages?.includes(l.id),
  )
  const languageOptions = options.supportedLanguages.filter(
    (l) => !options.defaultLanguages?.includes(l.id),
  )
  const [open, setOpen] = useState(false)
  const {selectableLanguages, selectedLanguages, selectAll, selectNone, toggleLanguage} =
    usePaneLanguages({options})
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  const handleToggleAll = useCallback(
    // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
    (event: FormEvent<HTMLInputElement>) => {
      const checked = event.currentTarget.checked

      if (checked) {
        selectAll()
      } else {
        selectNone()
      }
    },
    [selectAll, selectNone],
  )

  const handleClick = useCallback(() => setOpen((o) => !o), [])

  useClickOutsideEvent(
    () => setOpen(false),
    () => [buttonRef.current, popoverRef.current],
  )

  const allSelected = selectedLanguages.length === selectableLanguages.length

  const content = (
    <Box overflow="auto" padding={1}>
      {defaultLanguages.length > 0 && (
        <Card radius={2} tone="primary">
          <Flex padding={2} gap={3} flexDirection="column">
            <Text
              size={1}
              weight="semibold"
              as="div"
              trim={true}
              style={{color: 'var(--card-fg-color)'}}
            >
              Default language{defaultLanguages.length > 1 && <>s</>}
            </Text>

            {defaultLanguages.map((l) => (
              <Text key={l.id} as="div" trim={true} style={{color: 'var(--card-fg-color)'}}>
                {l.title}
              </Text>
            ))}
          </Flex>
        </Card>
      )}

      <Flex marginTop={3} padding={2} gap={2} flexDirection="column">
        <Box paddingBottom={1}>
          <Text size={1} weight="semibold" as="div" trim={true}>
            Show translations
          </Text>
        </Box>

        <Card as="label">
          <Flex alignItems="center" gap={2}>
            <Checkbox checked={allSelected} name="_allSelected" onChange={handleToggleAll} />
            <Box flexBasis="0%" flexGrow={1}>
              <Text muted={!allSelected} weight="semibold" as="div" trim={true}>
                All translations
              </Text>
            </Box>
          </Flex>
        </Card>

        {languageOptions.map((lang) => (
          <LanguageFilterOption
            key={lang.id}
            id={lang.id}
            onToggle={toggleLanguage}
            selected={selectedLanguages.includes(lang.id)}
            title={lang.title}
          />
        ))}
      </Flex>
    </Box>
  )

  return (
    <Popover constrainSize content={content} open={open} portal ref={popoverRef}>
      <Button
        icon={TranslateIcon}
        mode="bleed"
        onClick={handleClick}
        ref={buttonRef}
        selected={open}
      />
    </Popover>
  )
}

function LanguageFilterOption(props: {
  id: string
  onToggle: (id: string) => void
  selected: boolean
  title: string
}) {
  const {id, onToggle, selected, title} = props

  const handleChange = useCallback(() => {
    onToggle(id)
  }, [id, onToggle])

  return (
    <Card as="label">
      <Flex alignItems="center" gap={2}>
        <Checkbox checked={selected} name={`language-${id}`} onChange={handleChange} />
        <Box flexBasis="0%" flexGrow={1}>
          <Text muted={!selected} as="div" trim={true}>
            {title}
          </Text>
        </Box>
      </Flex>
    </Card>
  )
}
